import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  notification,
  Tag,
  Tooltip,
  Row,
  Col,
  Statistic,
  Tabs,
  Typography,
  Progress,
  Popconfirm,
  Empty,
  Badge,
  Upload,
  Alert
} from 'antd';
import {
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  ReloadOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  SafetyCertificateOutlined,
  UploadOutlined,
  DashboardOutlined,
  CloudServerOutlined,
  GlobalOutlined,
  ShoppingCartOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import API from '../services/api';

const { TabPane } = Tabs;
const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;


// 常量配置
const CONSTANTS = {
  TABS: {
    OVERVIEW: 'overview',
    IP_SERVICES: 'ip-services',
    DOMAINS: 'domains'
  },
  PAYMENT_METHODS: {
    alipay: '支付宝',
    admin_grant: '管理员赠送'
  },
  DOMAIN_PROTOCOLS: {
    http: 'HTTP',
    https: 'HTTPS'
  }
};

// 页面样式
const styles = {
  container: {
    padding: '24px',
    backgroundColor: '#f0f2f5',
    minHeight: 'calc(100vh - 64px)'
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px'
  },
  searchFilters: {
    marginBottom: '16px',
    padding: '16px',
    backgroundColor: '#fafafa',
    borderRadius: '6px'
  },
  statCard: {
    textAlign: 'center'
  }
};

// 域名额度来源相关工具函数
const getDomainStatus = (domain) => {
  // 优先使用quotaType判断，fallback到quotaSource保持兼容性
  if (domain.quotaType) {
    switch (domain.quotaType) {
      case 'gift':
        return {
          label: '赠送额度',
          color: 'green',
          description: '前5个域名使用基础防护赠送的额度，跟随基础防护服务到期时间，无需单独续费'
        };
      case 'extra':
        if (domain.quotaSource === 'renewed') {
          return {
            label: '独立续费',
            color: 'orange',
            description: '此域名已单独续费，拥有独立的到期时间'
          };
        }
        return {
          label: '额外购买',
          color: 'blue',
          description: '第6个及以后的域名使用额外购买的额度，可以单独续费'
        };
      default:
        return {
          label: '未知类型',
          color: 'gray',
          description: '未知的额度类型'
        };
    }
  }

  // 兼容旧版本API - fallback逻辑
  switch (domain.quotaSource) {
    case 'quota':
      return {
        label: '额度域名',
        color: 'blue',
        description: '通过WAF额度添加，跟随基础防护到期时间'
      };
    case 'renewed':
      return {
        label: '独立续费',
        color: 'orange',
        description: '已单独续费，拥有独立的到期时间'
      };
    default:
      return {
        label: '额度域名',
        color: 'blue',
        description: '通过WAF额度添加，跟随基础防护到期时间'
      };
  }
};

const getDisplayExpiredAt = (domain) => {
  // 如果有独立到期时间，优先显示独立到期时间
  if (domain.hasIndependentExpiry && domain.independentExpiredAt) {
    return domain.independentExpiredAt;
  }
  // 否则显示跟随基础防护的到期时间
  return domain.expiredAt;
};

const shouldShowRenewButton = (domain) => {
  // 优先使用canRenew字段（如果存在）
  if (domain.canRenew !== undefined) {
    return domain.canRenew === true;
  }

  // Fallback逻辑：只有额外购买额度(extra)的域名才能续费
  return domain.quotaType === 'extra';
};

const getRenewMessage = (domain) => {
  // 优先使用API返回的续费信息
  if (domain.renewInfo?.message) {
    return domain.renewInfo.message;
  }

  // Fallback逻辑：根据quotaType生成提示信息
  if (domain.quotaType === 'gift') {
    return '此域名使用赠送额度，跟随基础防护服务到期时间，无需单独续费';
  } else if (domain.quotaType === 'extra') {
    if (domain.quotaSource === 'renewed') {
      return '此域名已单独续费，拥有独立的到期时间';
    }
    return '此域名使用额外购买额度，可以单独续费以延长保护时间';
  }

  return '可以续费此域名';
};

// IP状态判断函数
const getIPStatus = (ip) => {
  if (!ip.hasWafService || !ip.wafService) {
    return 'unpurchased';
  }

  const { wafService } = ip;

  // 检查是否过期
  const isExpired = wafService.expiredAt && dayjs(wafService.expiredAt).isBefore(dayjs());
  if (isExpired) {
    return 'expired';
  }

  // 检查是否即将到期（7天内）
  const isExpiringSoon = wafService.expiredAt &&
    dayjs(wafService.expiredAt).diff(dayjs(), 'day') <= 7 &&
    dayjs(wafService.expiredAt).diff(dayjs(), 'day') >= 0;
  if (isExpiringSoon) {
    return 'expiring';
  }

  // 检查是否启用
  if (wafService.isEnabled) {
    return 'active';
  }

  return 'paused';
};

const WafManagement = () => {
  // 通用状态
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(CONSTANTS.TABS.OVERVIEW);
  const [refreshing, setRefreshing] = useState(false);

  // 概览数据
  const [overview, setOverview] = useState({
    services: [],
    stats: {}
  });

  // IP服务管理状态
  const [availableIPs, setAvailableIPs] = useState([]);
  const [ipsPagination, setIPsPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [ipsFilters, setIPsFilters] = useState({
    search: '',
    status: undefined
  });

  // 域名管理状态
  const [selectedIP, setSelectedIP] = useState(null);
  const [domains, setDomains] = useState([]);
  const [domainsPagination, setDomainsPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [domainsFilters, setDomainsFilters] = useState({
    search: '',
    isEnabled: undefined
  });
  const [domainModalVisible, setDomainModalVisible] = useState(false);
  const [domainForm] = Form.useForm();
  const [editingDomain, setEditingDomain] = useState(null);

  // SSL证书管理状态
  const [sslModalVisible, setSSLModalVisible] = useState(false);
  const [sslForm] = Form.useForm();
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [sslUploadMethod, setSSLUploadMethod] = useState('text'); // 'text' 或 'file'



  // 分离的弹窗状态
  const [basicProtectionModalVisible, setBasicProtectionModalVisible] = useState(false);
  const [extraQuotaModalVisible, setExtraQuotaModalVisible] = useState(false);
  const [domainRenewModalVisible, setDomainRenewModalVisible] = useState(false);

  // 分离的表单
  const [basicProtectionForm] = Form.useForm();
  const [extraQuotaForm] = Form.useForm();
  const [domainRenewForm] = Form.useForm();

  const [isRenewal, setIsRenewal] = useState(false); // 是否为续费操作

  // 使用帮助弹窗状态
  const [helpModalVisible, setHelpModalVisible] = useState(false);



  // 获取用户WAF服务概览
  const fetchOverview = useCallback(async () => {
    try {
      const response = await API.get('/api/waf/overview');
      if (response.data.code === 0) {
        setOverview(response.data.data);
      } else {
        message.error(response.data.msg || '获取概览数据失败');
      }
    } catch (error) {
      console.error('获取概览数据失败:', error);
      const errorMessage = error.response?.data?.msg || error.message || '获取概览数据失败';
      message.error(errorMessage);
    }
  }, []);

  // 获取可用IP列表
  const fetchAvailableIPs = useCallback(async (customParams = {}) => {
    setLoading(true);
    try {
      const requestParams = {
        page: 1,
        limit: 10,
        ...customParams
      };

      const response = await API.get('/api/waf/available-ips', { params: requestParams });
      if (response.data.code === 0) {
        const { ips, pagination } = response.data.data;

        // 客户端筛选
        let filteredIPs = ips;
        if (customParams.status) {
          filteredIPs = ips.filter(ip => {
            const status = getIPStatus(ip);
            return status === customParams.status;
          });
        }

        setAvailableIPs(filteredIPs);
        setIPsPagination({
          current: pagination.page,
          pageSize: pagination.limit,
          total: filteredIPs.length
        });
      } else {
        message.error(response.data.msg || '获取IP列表失败');
      }
    } catch (error) {
      console.error('获取IP列表失败:', error);
      const errorMessage = error.response?.data?.msg || error.message || '获取IP列表失败';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);



  // 获取域名列表
  const fetchDomains = useCallback(async (ip, customParams = {}) => {
    if (!ip) return;

    setLoading(true);
    try {
      const requestParams = {
        page: 1,
        limit: 10,
        ...customParams
      };

      const response = await API.get(`/api/waf/domains/${ip}`, { params: requestParams });
      if (response.data.code === 0) {
        const { domains, pagination } = response.data.data;
        setDomains(domains);
        setDomainsPagination({
          current: pagination.page,
          pageSize: pagination.limit,
          total: pagination.total
        });
      } else {
        message.error(response.data.msg || '获取域名列表失败');
      }
    } catch (error) {
      console.error('获取域名列表失败:', error);
      const errorMessage = error.response?.data?.msg || error.message || '获取域名列表失败';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // 添加/编辑域名
  const handleDomainSubmit = async (values) => {
    try {
      let response;
      if (editingDomain) {
        // 编辑域名时，直接发送更新数据
        response = await API.put(`/api/waf/domains/${editingDomain._id}`, values);
      } else {
        // 添加域名时，包含IP地址
        response = await API.post('/api/waf/domains', { ...values, ip: selectedIP });
      }

      if (response.data.code === 0) {
        message.success(editingDomain ? '域名更新成功' : '域名添加成功');
        setDomainModalVisible(false);
        domainForm.resetFields();
        setEditingDomain(null);
        fetchDomains(selectedIP, { page: 1, limit: 10 });
        fetchOverview();
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      console.error('域名操作失败:', error);
      message.error(error.response?.data?.msg || '域名操作失败');
    }
  };

  // 删除域名
  const handleDeleteDomain = async (domainId) => {
    try {
      const response = await API.delete(`/api/waf/domains/${domainId}`);
      if (response.data.code === 0) {
        message.success('域名删除成功');
        fetchDomains(selectedIP, { page: 1, limit: 10 });
        fetchOverview();
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      console.error('删除域名失败:', error);
      const errorMessage = error.response?.data?.msg || error.message || '删除域名失败';
      message.error(errorMessage);
    }
  };

  // 切换域名状态
  const handleToggleDomainStatus = async (domainId, currentStatus) => {
    try {
      const response = await API.put(`/api/waf/domains/${domainId}`, {
        isEnabled: !currentStatus
      });
      if (response.data.code === 0) {
        message.success(`域名已${!currentStatus ? '启用' : '禁用'}`);
        fetchDomains(selectedIP, { page: 1, limit: 10 });
        fetchOverview();
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      console.error('切换域名状态失败:', error);
      const errorMessage = error.response?.data?.msg || error.message || '切换域名状态失败';
      message.error(errorMessage);
    }
  };

  // 通用支付处理函数
  const handlePaymentSuccess = (order) => {
    message.success(`订单创建成功！订单号：${order.orderId}，正在跳转到支付页面...`);
    fetchOverview();

    // 如果当前在IP服务页面，同时刷新IP列表
    if (activeTab === CONSTANTS.TABS.IP_SERVICES) {
      fetchAvailableIPs({
        ...ipsFilters,
        page: ipsPagination.current,
        limit: ipsPagination.pageSize
      });
    }

    // 跳转到支付宝支付页面
    if (order.paymentUrl) {
      // 显示跳转提示
      notification.info({
        message: '正在跳转支付页面',
        description: `订单金额：￥${order.totalAmount}，如页面未自动跳转，请检查浏览器弹窗设置`,
        duration: 5
      });
      // 创建新窗口进行支付
      const paymentWindow = window.open('', '_blank');
      if (paymentWindow) {
        paymentWindow.document.open();
        paymentWindow.document.write(order.paymentUrl);
        paymentWindow.document.close();
      } else {
        // 如果弹窗被阻止，提供备选方案
        Modal.confirm({
          title: '跳转到支付页面',
          content: (
            <div>
              <p>订单已创建成功，点击确定跳转到支付宝完成支付。</p>
              <p>订单号：{order.orderId}</p>
              <p>金额：￥{order.totalAmount}</p>
            </div>
          ),
          onOk() {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = order.paymentUrl;
            document.body.appendChild(tempDiv);
            // 查找并提交表单
            const form = tempDiv.querySelector('form');
            if (form) {
              form.submit();
            }
            document.body.removeChild(tempDiv);
          },
          okText: '去支付',
          cancelText: '稍后支付'
        });
      }
    }
  };

  // 基础防护购买/续费
  const handleBasicProtectionPurchase = async (values) => {
    try {
      const targetIP = selectedIP || values.ip;
      if (!targetIP) {
        message.error('请先选择IP地址');
        return;
      }

      const requestData = {
        ip: targetIP,
        duration: values.duration,
        paymentMethod: values.paymentMethod || 'alipay'
      };

      const response = await API.post('/api/waf/purchase-basic-protection', requestData);

      if (response.data.code === 0) {
        const { order } = response.data.data;
        setBasicProtectionModalVisible(false);
        basicProtectionForm.resetFields();
        setIsRenewal(false);
        handlePaymentSuccess(order);
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      console.error('购买基础防护失败:', error);
      const errorMessage = error.response?.data?.msg || error.message || '购买基础防护失败';
      message.error(errorMessage);
    }
  };

  // 额外额度购买
  const handleExtraQuotaPurchase = async (values) => {
    try {
      const targetIP = selectedIP || values.ip;
      if (!targetIP) {
        message.error('请先选择IP地址');
        return;
      }

      const requestData = {
        ip: targetIP,
        quotaCount: values.quotaCount,
        paymentMethod: values.paymentMethod || 'alipay'
      };

      const response = await API.post('/api/waf/purchase-extra-quota', requestData);

      if (response.data.code === 0) {
        const { order } = response.data.data;
        setExtraQuotaModalVisible(false);
        extraQuotaForm.resetFields();
        handlePaymentSuccess(order);
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      console.error('购买额外额度失败:', error);
      const errorMessage = error.response?.data?.msg || error.message || '购买额外额度失败';
      message.error(errorMessage);
    }
  };

  // 域名续费
  const handleDomainRenew = async (values) => {
    try {
      const targetIP = selectedIP || values.ip;
      if (!targetIP) {
        message.error('请先选择IP地址');
        return;
      }

      const requestData = {
        ip: targetIP,
        domainId: values.domainId,
        duration: values.duration,
        paymentMethod: values.paymentMethod || 'alipay'
      };

      const response = await API.post('/api/waf/renew-domain', requestData);

      if (response.data.code === 0) {
        const { order } = response.data.data;
        setDomainRenewModalVisible(false);
        domainRenewForm.resetFields();
        handlePaymentSuccess(order);

        // 刷新域名列表
        if (selectedIP) {
          fetchDomains(selectedIP, { page: domainsPagination.current });
        }
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      console.error('域名续费失败:', error);
      const errorMessage = error.response?.data?.msg || error.message || '域名续费失败';
      message.error(errorMessage);
    }
  };



  // 初始化 - 仅在切换tab时加载数据
  useEffect(() => {
    const loadData = async () => {
      if (activeTab === CONSTANTS.TABS.OVERVIEW) {
        try {
          const response = await API.get('/api/waf/overview');
          if (response.data.code === 0) {
            setOverview(response.data.data);
          } else {
            message.error(response.data.msg || '获取概览数据失败');
          }
        } catch (error) {
          console.error('获取概览数据失败:', error);
          const errorMessage = error.response?.data?.msg || error.message || '获取概览数据失败';
          message.error(errorMessage);
        }
      } else if (activeTab === CONSTANTS.TABS.IP_SERVICES) {
        // 重置分页到第一页并加载数据
        setIPsPagination(prev => ({ ...prev, current: 1 }));
        fetchAvailableIPs({ page: 1 });
      } else if (activeTab === CONSTANTS.TABS.DOMAINS && selectedIP) {
        // 重置分页到第一页并加载数据
        setDomainsPagination(prev => ({ ...prev, current: 1 }));
        fetchDomains(selectedIP, { page: 1 });
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedIP]);

  // IP服务表格列定义
  const ipColumns = [
    {
      title: 'IP地址',
      dataIndex: 'ip',
      key: 'ip',
      render: (text) => <Text code>{text}</Text>
    },
    {
      title: '分配给',
      dataIndex: 'assignedTo',
      key: 'assignedTo',
      render: (user) => user ? (
        <div>
          <div>{user.username}</div>
          <Text type="secondary">{user.email}</Text>
        </div>
      ) : '-'
    },
    {
      title: '防护状态',
      key: 'wafStatus',
      render: (_, record) => {
        if (record.hasWafService && record.wafService) {
          const { wafService } = record;

          // 检查是否过期
          const isExpired = wafService.expiredAt && dayjs(wafService.expiredAt).isBefore(dayjs());
          // 检查是否即将到期（7天内）
          const isExpiringSoon = wafService.expiredAt &&
            dayjs(wafService.expiredAt).diff(dayjs(), 'day') <= 7 &&
            dayjs(wafService.expiredAt).diff(dayjs(), 'day') >= 0;

          if (isExpired) {
            return <Tag color="red">已过期</Tag>;
          }

          if (isExpiringSoon) {
            const daysLeft = dayjs(wafService.expiredAt).diff(dayjs(), 'day');
            return (
              <Tooltip title={`${daysLeft}天后到期，建议及时续费`}>
                <Tag color="orange">即将到期</Tag>
              </Tooltip>
            );
          }

          if (wafService.isEnabled) {
            return <Tag color="success">防护中</Tag>;
          }

          return <Tag color="default">已暂停</Tag>;
        }
        return <Tag color="default">未购买</Tag>;
      }
    },
    {
      title: '域名额度',
      key: 'quota',
      render: (_, record) => {
        if (record.hasWafService && record.wafService) {
          const { wafService } = record;
          const percent = wafService.totalQuota > 0 ?
            Math.round((wafService.usedQuota / wafService.totalQuota) * 100) : 0;

          return (
            <div>
              <Progress percent={percent} size="small" />
              <div style={{ fontSize: '12px', marginTop: '2px' }}>
                <Text type="secondary">
                  已用: {wafService.usedQuota || 0}/{wafService.totalQuota || 0}
                </Text>
              </div>
            </div>
          );
        }
        return '-';
      }
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          {!record.hasWafService ? (
            <Button
              type="primary"
              size="small"
              icon={<ShoppingCartOutlined />}
              onClick={() => {
                setIsRenewal(false);
                basicProtectionForm.resetFields();
                basicProtectionForm.setFieldsValue({ ip: record.ip });
                setBasicProtectionModalVisible(true);
              }}
            >
              购买基础防护
            </Button>
          ) : (
            <>
              <Button
                size="small"
                icon={<GlobalOutlined />}
                onClick={() => {
                  setSelectedIP(record.ip);
                  setActiveTab(CONSTANTS.TABS.DOMAINS);
                }}
              >
                域名管理
              </Button>
              <Button
                size="small"
                icon={<ShoppingCartOutlined />}
                onClick={() => {
                  setIsRenewal(true);
                  basicProtectionForm.resetFields();
                  basicProtectionForm.setFieldsValue({ ip: record.ip });
                  setBasicProtectionModalVisible(true);
                }}
              >
                续费防护
              </Button>
            </>
          )}
        </Space>
      )
    }
  ];

  return (
    <div style={styles.container}>
      {/* 页面标题 */}
      <div style={styles.pageHeader}>
        <div>
          <Title level={2}>
            <CloudServerOutlined />
            WAF防护管理
          </Title>
        </div>

        <Button
          type="primary"
          icon={<ReloadOutlined spin={refreshing} />}
          loading={refreshing}
          onClick={() => {
            setRefreshing(true);
            setTimeout(() => {
              if (activeTab === CONSTANTS.TABS.OVERVIEW) {
                fetchOverview();
              } else if (activeTab === CONSTANTS.TABS.IP_SERVICES) {
                fetchAvailableIPs({
                  ...ipsFilters,
                  page: ipsPagination.current,
                  limit: ipsPagination.pageSize
                });
              } else if (activeTab === CONSTANTS.TABS.DOMAINS && selectedIP) {
                fetchDomains(selectedIP, {
                  ...domainsFilters,
                  page: domainsPagination.current,
                  limit: domainsPagination.pageSize
                });
              }
              setRefreshing(false);
            }, 1000);
          }}
        >
          刷新数据
        </Button>
      </div>

      {/* 页面内容区域 */}
      <div>
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
        >
        {/* 服务概览 */}
        <TabPane
          tab={
            <span>
              <DashboardOutlined />
              服务概览
            </span>
          }
          key={CONSTANTS.TABS.OVERVIEW}
        >
          <Row gutter={16} style={{ marginBottom: '24px' }}>
            <Col span={4}>
              <Card style={styles.statCard}>
                <Statistic
                  title="防护服务数"
                  value={overview.stats.totalServices || 0}
                  prefix={<CloudServerOutlined />}
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card style={styles.statCard}>
                <Statistic
                  title="防护中IP"
                  value={overview.stats.enabledServices || 0}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card style={styles.statCard}>
                <Statistic
                  title="总域名数"
                  value={overview.stats.totalDomains || 0}
                  prefix={<GlobalOutlined />}
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card style={styles.statCard}>
                <Statistic
                  title="总额度"
                  value={overview.stats.totalQuota || 0}
                  prefix={<BarChartOutlined />}
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card style={styles.statCard}>
                <Statistic
                  title="已用额度"
                  value={overview.stats.usedQuota || 0}
                  prefix={<BarChartOutlined />}
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card style={styles.statCard}>
                <Statistic
                  title="即将到期"
                  value={overview.stats.expiringSoon || 0}
                  valueStyle={{ color: '#ff4d4f' }}
                  prefix={<SafetyCertificateOutlined />}
                />
              </Card>
            </Col>
          </Row>



          <Card
            title="快捷操作"
            style={{ marginBottom: '16px' }}
          >
            <Space size="large">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setActiveTab(CONSTANTS.TABS.IP_SERVICES)}
              >
                购买基础防护
              </Button>
              <Button
                icon={<GlobalOutlined />}
                onClick={() => setActiveTab(CONSTANTS.TABS.DOMAINS)}
              >
                管理域名
              </Button>
              <Button
                icon={<QuestionCircleOutlined />}
                onClick={() => setHelpModalVisible(true)}
              >
                使用帮助
              </Button>
              {overview.stats?.expiringSoon > 0 && (
                <Button
                  type="dashed"
                  danger
                  icon={<ShoppingCartOutlined />}
                  onClick={() => {
                    message.info(`您有${overview.stats.expiringSoon}个防护服务即将到期，请前往各服务页面进行续费操作`);
                  }}
                >
                  {overview.stats.expiringSoon}个防护即将到期
                </Button>
              )}
            </Space>
          </Card>

          <Card title="我的防护服务">
            <Table
              columns={[
                {
                  title: 'IP地址',
                  dataIndex: 'ip',
                  key: 'ip',
                  render: (text) => <Text code>{text}</Text>
                },

                {
                  title: '防护状态',
                  key: 'protectionStatus',
                  render: (_, record) => {
                    if (!record.hasBasicProtection) {
                      return <Badge status="default" text="未购买" />;
                    }
                    if (!record.isBasicProtectionActive) {
                      return <Badge status="warning" text="已过期" />;
                    }
                    if (record.isEnabled) {
                      return <Badge status="success" text="防护中" />;
                    }
                    return <Badge status="default" text="已暂停" />;
                  }
                },
                {
                  title: '额度使用',
                  key: 'quota',
                  render: (_, record) => {
                    const percent = record.totalQuota > 0 ?
                      Math.round((record.usedQuota / record.totalQuota) * 100) : 0;

                    return (
                      <div>
                        <Progress percent={percent} size="small" />
                        <div style={{ fontSize: '12px', marginTop: '4px' }}>
                          <Text type="secondary">
                            已用: {record.usedQuota}/{record.totalQuota}
                          </Text>
                          {record.basicQuota && record.extraQuota !== undefined && (
                            <div style={{ fontSize: '11px', color: '#999' }}>
                              基础: {record.basicQuota} | 额外: {record.extraQuota || 0}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }
                },
                {
                  title: '基础防护到期',
                  key: 'expiredAt',
                  render: (_, record) => {
                    const date = record.basicProtectionExpiredAt || record.expiredAt;
                    if (!date) return '-';
                    const isExpired = dayjs(date).isBefore(dayjs());
                    const daysLeft = dayjs(date).diff(dayjs(), 'day');

                    let textType = 'secondary';
                    if (isExpired) {
                      textType = 'danger';
                    } else if (daysLeft <= 7) {
                      textType = 'warning';
                    }

                    return (
                      <div>
                        <Text type={textType}>
                          {dayjs(date).format('YYYY-MM-DD')}
                        </Text>
                        {!isExpired && daysLeft <= 30 && (
                          <div style={{ fontSize: '11px', color: '#999' }}>
                            {daysLeft}天后到期
                          </div>
                        )}
                        <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                          (影响额度域名)
                        </div>
                      </div>
                    );
                  }
                },
                {
                  title: '操作',
                  key: 'action',
                  render: (_, record) => (
                    <Space size="small">
                      <Button
                        size="small"
                        icon={<GlobalOutlined />}
                        onClick={() => {
                          setSelectedIP(record.ip);
                          setActiveTab(CONSTANTS.TABS.DOMAINS);
                        }}
                      >
                        管理
                      </Button>
                      <Button
                        size="small"
                        type="primary"
                        icon={<ShoppingCartOutlined />}
                        onClick={() => {
                          setIsRenewal(true);
                          basicProtectionForm.resetFields();
                          basicProtectionForm.setFieldsValue({ ip: record.ip });
                          setBasicProtectionModalVisible(true);
                        }}
                      >
                        续费
                      </Button>
                    </Space>
                  )
                }
              ]}
              dataSource={overview.services || []}
              rowKey="_id"
              locale={{
                emptyText: (
                  <Empty
                    description="暂无防护服务，点击上方快捷操作购买基础防护"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                )
              }}
              pagination={false}
            />
          </Card>
        </TabPane>

        {/* IP服务管理 */}
        <TabPane
          tab={
            <span>
              <CloudServerOutlined />
              IP服务管理
            </span>
          }
          key={CONSTANTS.TABS.IP_SERVICES}
        >
          <Card>
            <div style={styles.searchFilters}>
              <Row gutter={16} align="middle">
                <Col span={8}>
                  <Input
                    placeholder="搜索IP地址"
                    prefix={<SearchOutlined />}
                    value={ipsFilters.search}
                    onChange={(e) => {
                      setIPsFilters(prev => ({ ...prev, search: e.target.value }));
                    }}
                    onPressEnter={() => {
                      setIPsPagination(prev => ({ ...prev, current: 1 }));
                      fetchAvailableIPs({ ...ipsFilters, page: 1, limit: ipsPagination.pageSize });
                    }}
                    allowClear
                  />
                </Col>
                <Col span={6}>
                  <Select
                    placeholder="防护状态"
                    allowClear
                    value={ipsFilters.status}
                    onChange={(value) => {
                      setIPsFilters(prev => ({ ...prev, status: value }));
                    }}
                    style={{ width: '100%' }}
                  >
                    <Option value="unpurchased">未购买</Option>
                    <Option value="active">防护中</Option>
                    <Option value="paused">已暂停</Option>
                    <Option value="expired">已过期</Option>
                    <Option value="expiring">即将到期</Option>
                  </Select>
                </Col>
                <Col span={5}>
                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={() => {
                      setIPsPagination(prev => ({ ...prev, current: 1 }));
                      fetchAvailableIPs({ ...ipsFilters, page: 1, limit: ipsPagination.pageSize });
                    }}
                    block
                  >
                    搜索
                  </Button>
                </Col>
                <Col span={5}>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => {
                      setIPsPagination(prev => ({ ...prev, current: 1 }));
                      fetchAvailableIPs({ page: 1, limit: ipsPagination.pageSize });
                    }}
                    block
                  >
                    刷新
                  </Button>
                </Col>
              </Row>
            </div>

            <Table
              columns={ipColumns}
              dataSource={availableIPs}
              rowKey="ip"
              loading={loading}
              locale={{
                emptyText: (
                  <Empty
                    description="暂无可用IP，请联系管理员分配IP资源"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                )
              }}
              pagination={{
                ...ipsPagination,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
                onChange: (page, pageSize) => {
                  setIPsPagination(prev => ({ ...prev, current: page, pageSize }));
                  fetchAvailableIPs({ ...ipsFilters, page, limit: pageSize });
                }
              }}
            />
          </Card>
        </TabPane>

        {/* 域名管理 */}
        <TabPane
          tab={
            <span>
              <GlobalOutlined />
              域名管理
            </span>
          }
          key={CONSTANTS.TABS.DOMAINS}
        >
          <Card
            title={selectedIP ? `${selectedIP} - 域名管理` : '域名管理'}
            extra={
              selectedIP && (
                <Space>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setEditingDomain(null);
                      domainForm.resetFields();
                      // 添加域名时自动设置源服务器地址为当前选择的IP，协议默认为http，端口默认为80
                      domainForm.setFieldsValue({
                        originServer: selectedIP,
                        protocol: 'http',
                        originPort: 80
                      });
                      setDomainModalVisible(true);
                    }}
                  >
                    添加域名
                  </Button>
                  <Button
                    icon={<ShoppingCartOutlined />}
                    onClick={() => {
                      extraQuotaForm.resetFields();
                      setExtraQuotaModalVisible(true);
                    }}
                  >
                    购买额外额度(10元/站点/月)
                  </Button>
                </Space>
              )
            }
          >
            {!selectedIP ? (
              <Alert
                message="请先选择IP"
                description={
                  <div>
                    <div>请从IP服务管理页面选择一个已购买基础防护的IP，然后进行域名管理。</div>
                    <Button
                      type="link"
                      onClick={() => setActiveTab(CONSTANTS.TABS.IP_SERVICES)}
                      style={{ padding: 0, marginTop: '8px' }}
                    >
                      前往IP服务管理
                    </Button>
                  </div>
                }
                type="info"
                showIcon
              />
            ) : (
              <>
                <Row gutter={16} style={{ marginBottom: '16px' }}>
                  <Col span={6}>
                    <Card size="small" style={styles.statCard}>
                      <Statistic
                        title="总域名数"
                        value={domainsPagination.total || 0}
                        prefix={<GlobalOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card size="small" style={styles.statCard}>
                      <Statistic
                        title="启用域名"
                        value={domains.filter(d => d.isEnabled).length || 0}
                        prefix={<CheckCircleOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card size="small" style={styles.statCard}>
                      <Statistic
                        title="SSL证书"
                        value={domains.filter(d => d.ssl?.hasCertificate).length || 0}
                        prefix={<SafetyCertificateOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card size="small" style={styles.statCard}>
                      <Statistic
                        title="HTTPS域名"
                        value={domains.filter(d => d.config?.protocol === 'https').length || 0}
                        prefix={<SafetyCertificateOutlined />}
                      />
                    </Card>
                  </Col>
                </Row>

                <div style={styles.searchFilters}>
                  <Row gutter={16} align="middle">
                    <Col span={8}>
                      <Input
                        placeholder="搜索域名"
                        prefix={<SearchOutlined />}
                        value={domainsFilters.search}
                        onChange={(e) => {
                          setDomainsFilters(prev => ({ ...prev, search: e.target.value }));
                        }}
                        onPressEnter={() => {
                          setDomainsPagination(prev => ({ ...prev, current: 1 }));
                          fetchDomains(selectedIP, { ...domainsFilters, page: 1, limit: domainsPagination.pageSize });
                        }}
                        allowClear
                      />
                    </Col>
                    <Col span={4}>
                      <Select
                        placeholder="状态"
                        allowClear
                        value={domainsFilters.isEnabled}
                        onChange={(value) => {
                          setDomainsFilters(prev => ({ ...prev, isEnabled: value }));
                        }}
                        style={{ width: '100%' }}
                      >
                        <Option value={true}>启用</Option>
                        <Option value={false}>禁用</Option>
                      </Select>
                    </Col>
                    <Col span={4}>
                      <Button
                        type="primary"
                        icon={<SearchOutlined />}
                        onClick={() => {
                          setDomainsPagination(prev => ({ ...prev, current: 1 }));
                          fetchDomains(selectedIP, { ...domainsFilters, page: 1, limit: domainsPagination.pageSize });
                        }}
                        block
                      >
                        搜索
                      </Button>
                    </Col>
                    <Col span={4}>
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={() => {
                          setDomainsPagination(prev => ({ ...prev, current: 1 }));
                          fetchDomains(selectedIP, { page: 1, limit: domainsPagination.pageSize });
                        }}
                        block
                      >
                        刷新
                      </Button>
                    </Col>
                  </Row>
                </div>

                <Table
                  columns={[
                    {
                      title: '域名',
                      dataIndex: 'domain',
                      key: 'domain',
                      render: (text) => <Text code>{text}</Text>
                    },
                    {
                      title: '协议',
                      key: 'protocol',
                      render: (_, record) => {
                        const protocol = record.config?.protocol;
                        return (
                          <Tag color={protocol === 'https' ? 'green' : 'blue'}>
                            {CONSTANTS.DOMAIN_PROTOCOLS[protocol] || protocol}
                          </Tag>
                        );
                      }
                    },
                    {
                      title: '额度来源',
                      key: 'quotaSource',
                      render: (_, record) => {
                        const status = getDomainStatus(record);
                        return (
                          <Tooltip title={status.description}>
                            <Tag color={status.color}>{status.label}</Tag>
                          </Tooltip>
                        );
                      }
                    },
                    {
                      title: '源服务器',
                      key: 'origin',
                      render: (_, record) => (
                        <div>
                          <Text code>{record.config?.originServer}:{record.config?.originPort}</Text>
                        </div>
                      )
                    },
                    {
                      title: 'SSL证书',
                      key: 'ssl',
                      render: (_, record) => (
                        record.ssl?.hasCertificate ? (
                          <div>
                            <Badge status="success" text="已配置" />
                            {record.ssl.certificateExpiredAt && (
                              <div>
                                <Text type="secondary">
                                  到期: {dayjs(record.ssl.certificateExpiredAt).format('YYYY-MM-DD')}
                                </Text>
                              </div>
                            )}
                          </div>
                        ) : (
                          <Badge status="default" text="未配置" />
                        )
                      )
                    },
                    {
                      title: '状态',
                      dataIndex: 'isEnabled',
                      key: 'isEnabled',
                      render: (enabled) => (
                        <Badge
                          status={enabled ? 'success' : 'default'}
                          text={enabled ? '启用' : '禁用'}
                        />
                      )
                    },
                    {
                      title: '到期时间',
                      key: 'expiredAt',
                      render: (_, record) => {
                        const expiredAt = getDisplayExpiredAt(record);
                        if (!expiredAt) return <Text type="secondary">-</Text>;

                        const isExpired = dayjs(expiredAt).isBefore(dayjs());
                        const daysLeft = dayjs(expiredAt).diff(dayjs(), 'day');

                        let textType = 'secondary';
                        if (isExpired) {
                          textType = 'danger';
                        } else if (daysLeft <= 7) {
                          textType = 'warning';
                        }

                        return (
                          <div>
                            <Text type={textType}>
                              {dayjs(expiredAt).format('YYYY-MM-DD')}
                            </Text>
                            {record.hasIndependentExpiry && (
                              <div style={{ fontSize: '11px', color: '#52c41a' }}>
                                独立到期
                              </div>
                            )}
                            {!isExpired && daysLeft <= 30 && (
                              <div style={{ fontSize: '11px', color: '#999' }}>
                                {daysLeft}天后到期
                              </div>
                            )}
                          </div>
                        );
                      }
                    },
                    {
                      title: '操作',
                      key: 'action',
                      render: (_, record) => (
                        <Space size="small">
                          <Tooltip title={record.isEnabled ? '禁用域名' : '启用域名'}>
                            <Button
                              type="link"
                              icon={record.isEnabled ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                              size="small"
                              onClick={() => handleToggleDomainStatus(record._id, record.isEnabled)}
                            />
                          </Tooltip>
                          <Tooltip title="编辑域名">
                            <Button
                              type="link"
                              icon={<EditOutlined />}
                              size="small"
                                                             onClick={() => {
                                 setEditingDomain(record);
                                 const protocol = record.config?.protocol || 'http';
                                 const port = record.config?.originPort || (protocol === 'https' ? 443 : 80);
                                 domainForm.setFieldsValue({
                                   domain: record.domain,
                                   originServer: selectedIP, // 始终使用当前选择的IP
                                   originPort: port,
                                   protocol: protocol
                                 });
                                 setDomainModalVisible(true);
                               }}
                            />
                          </Tooltip>
                          <Tooltip title="SSL证书">
                            <Button
                              type="link"
                              icon={<SafetyCertificateOutlined />}
                              size="small"
                              onClick={() => {
                                setSelectedDomain(record);
                                setSSLModalVisible(true);
                              }}
                            />
                          </Tooltip>
                          {shouldShowRenewButton(record) && (
                            <Tooltip title={getRenewMessage(record)}>
                              <Button
                                type="link"
                                icon={<ShoppingCartOutlined />}
                                size="small"
                                onClick={() => {
                                  domainRenewForm.resetFields();
                                  domainRenewForm.setFieldsValue({ domainId: record._id });
                                  setDomainRenewModalVisible(true);
                                }}
                              />
                            </Tooltip>
                          )}
                          <Popconfirm
                            title="确定要删除这个域名吗？"
                            description="删除后将释放对应的域名额度"
                            onConfirm={() => handleDeleteDomain(record._id)}
                            okText="确定"
                            cancelText="取消"
                          >
                            <Tooltip title="删除域名">
                              <Button
                                type="link"
                                danger
                                icon={<DeleteOutlined />}
                                size="small"
                              />
                            </Tooltip>
                          </Popconfirm>
                        </Space>
                      )
                    }
                  ]}
                  dataSource={domains}
                  rowKey="_id"
                  loading={loading}
                  locale={{
                    emptyText: (
                      <Empty
                        description="暂无域名"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    )
                  }}
                  pagination={{
                    ...domainsPagination,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) =>
                      `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
                    onChange: (page, pageSize) => {
                      setDomainsPagination(prev => ({ ...prev, current: page, pageSize }));
                      fetchDomains(selectedIP, { ...domainsFilters, page, limit: pageSize });
                    }
                  }}
                />
              </>
            )}
          </Card>
        </TabPane>


      </Tabs>





      {/* 域名添加/编辑模态框 */}
      <Modal
        title={editingDomain ? '编辑域名' : '添加域名'}
        open={domainModalVisible}
        onCancel={() => {
          setDomainModalVisible(false);
          domainForm.resetFields();
          setEditingDomain(null);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={domainForm}
          layout="vertical"
          onFinish={handleDomainSubmit}
        >
          <Form.Item
            label="域名"
            name="domain"
            rules={[
              { required: true, message: '请输入域名' },
              { pattern: /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/, message: '请输入有效的域名' }
            ]}
          >
            <Input placeholder="例如: example.com" />
          </Form.Item>

          <Form.Item
            label="协议"
            name="protocol"
            initialValue="http"
            rules={[{ required: true, message: '请选择协议' }]}
          >
            <Select
              onChange={(value) => {
                // 根据协议自动设置端口
                setTimeout(() => {
                  if (value === 'https') {
                    domainForm.setFieldsValue({ originPort: 443 });
                  } else if (value === 'http') {
                    domainForm.setFieldsValue({ originPort: 80 });
                  }
                }, 0);
              }}
            >
              <Option value="http">HTTP</Option>
              <Option value="https">HTTPS</Option>
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                label="源服务器地址"
                name="originServer"
                rules={[
                  { required: true, message: '源服务器地址不能为空' }
                ]}
              >
                <Input
                  placeholder="源服务器IP地址"
                  readOnly
                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                />
                <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                  固定为当前选择的IP地址：{selectedIP}
                </div>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item dependencies={['protocol']} noStyle>
                {({ getFieldValue }) => {
                  const protocol = getFieldValue('protocol') || 'http';
                  const defaultPort = protocol === 'https' ? 443 : 80;
                  
                  return (
                    <Form.Item
                      label="端口"
                      name="originPort"
                      initialValue={defaultPort}
                      rules={[
                        { required: true, message: '请输入端口' },
                        { type: 'number', min: 1, max: 65535, message: '端口范围: 1-65535' }
                      ]}
                    >
                      <InputNumber
                        min={1}
                        max={65535}
                        placeholder={`端口 (默认: ${defaultPort})`}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
              <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                HTTP协议默认80端口，HTTPS协议默认443端口
              </div>
            </Col>
          </Row>

          <Alert
            message="配置说明"
            description="源服务器地址固定为当前选择的IP地址，端口会根据协议自动设置（HTTP为80，HTTPS为443），域名添加后请通过工单联系我们进行策略部署。HTTPS协议需要单独上传SSL证书。"
            type="info"
            style={{ marginBottom: '16px' }}
          />

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingDomain ? '更新域名' : '添加域名'}
              </Button>
              <Button onClick={() => {
                setDomainModalVisible(false);
                domainForm.resetFields();
                setEditingDomain(null);
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* SSL证书上传模态框 */}
      <Modal
        title={`SSL证书管理 - ${selectedDomain?.domain}`}
        open={sslModalVisible}
        onCancel={() => {
          setSSLModalVisible(false);
          sslForm.resetFields();
          setSelectedDomain(null);
          setSSLUploadMethod('text');
        }}
        footer={null}
        width={700}
      >
        {selectedDomain && (
          <div>
            {selectedDomain.ssl?.hasCertificate && (
              <Alert
                message="当前SSL状态"
                description={
                  <div>
                    <div>证书已配置</div>
                    {selectedDomain.ssl.certificateExpiredAt && (
                      <div>到期时间: {dayjs(selectedDomain.ssl.certificateExpiredAt).format('YYYY-MM-DD HH:mm:ss')}</div>
                    )}
                    {selectedDomain.ssl.uploadedAt && (
                      <div>上传时间: {dayjs(selectedDomain.ssl.uploadedAt).format('YYYY-MM-DD HH:mm:ss')}</div>
                    )}
                  </div>
                }
                type="success"
                style={{ marginBottom: '16px' }}
                action={
                  <Button
                    size="small"
                    danger
                    onClick={async () => {
                      try {
                        const response = await API.delete(`/api/waf/ssl/${selectedDomain._id}`);
                        if (response.data.code === 0) {
                          message.success('SSL证书删除成功');
                          setSSLModalVisible(false);
                          fetchDomains(selectedIP, { page: 1, limit: 10 });
                        } else {
                          message.error(response.data.msg);
                        }
                      } catch (error) {
                        console.error('删除SSL证书失败:', error);
                        const errorMessage = error.response?.data?.msg || error.message || '删除SSL证书失败';
                        message.error(errorMessage);
                      }
                    }}
                  >
                    删除证书
                  </Button>
                }
              />
            )}

            <div style={{ marginBottom: '16px' }}>
              <Text strong>上传方式：</Text>
              <div style={{ marginTop: '8px' }}>
                <Button.Group>
                  <Button
                    type={sslUploadMethod === 'text' ? 'primary' : 'default'}
                    onClick={() => setSSLUploadMethod('text')}
                  >
                    文本输入
                  </Button>
                  <Button
                    type={sslUploadMethod === 'file' ? 'primary' : 'default'}
                    onClick={() => setSSLUploadMethod('file')}
                  >
                    文件上传
                  </Button>
                </Button.Group>
              </div>
            </div>

            <Form
              form={sslForm}
              layout="vertical"
              onFinish={async (values) => {
                try {
                  let formData = new FormData();

                  if (sslUploadMethod === 'file') {
                    if (values.certificateFile && values.privateKeyFile) {
                      formData.append('certificate', values.certificateFile[0].originFileObj);
                      formData.append('privateKey', values.privateKeyFile[0].originFileObj);
                    } else {
                      message.error('请选择证书文件和私钥文件');
                      return;
                    }
                  } else {
                    formData.append('certificateText', values.certificateText);
                    formData.append('privateKeyText', values.privateKeyText);
                  }

                  const response = await API.post(`/api/waf/ssl/${selectedDomain._id}`, formData, {
                    headers: {
                      'Content-Type': 'multipart/form-data',
                    },
                  });

                  if (response.data.code === 0) {
                    message.success('SSL证书上传成功');
                    setSSLModalVisible(false);
                    sslForm.resetFields();
                    fetchDomains(selectedIP, { page: 1, limit: 10 });
                  } else {
                    message.error(response.data.msg);
                  }
                } catch (error) {
                  console.error('上传SSL证书失败:', error);
                  const errorMessage = error.response?.data?.msg || error.message || '上传SSL证书失败';
                  message.error(errorMessage);
                }
              }}
            >
              {sslUploadMethod === 'text' ? (
                <>
                  <Form.Item
                    label="SSL证书内容"
                    name="certificateText"
                    rules={[{ required: true, message: '请输入SSL证书内容' }]}
                  >
                    <TextArea
                      rows={8}
                      placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                    />
                  </Form.Item>

                  <Form.Item
                    label="私钥内容"
                    name="privateKeyText"
                    rules={[{ required: true, message: '请输入私钥内容' }]}
                  >
                    <TextArea
                      rows={8}
                      placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
                    />
                  </Form.Item>
                </>
              ) : (
                <>
                  <Form.Item
                    label="SSL证书文件"
                    name="certificateFile"
                    valuePropName="fileList"
                    getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList}
                    rules={[{ required: true, message: '请选择SSL证书文件' }]}
                  >
                    <Upload
                      beforeUpload={() => false}
                      maxCount={1}
                      accept=".pem,.crt,.cer"
                    >
                      <Button icon={<UploadOutlined />}>选择证书文件</Button>
                    </Upload>
                  </Form.Item>

                  <Form.Item
                    label="私钥文件"
                    name="privateKeyFile"
                    valuePropName="fileList"
                    getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList}
                    rules={[{ required: true, message: '请选择私钥文件' }]}
                  >
                    <Upload
                      beforeUpload={() => false}
                      maxCount={1}
                      accept=".pem,.key"
                    >
                      <Button icon={<UploadOutlined />}>选择私钥文件</Button>
                    </Upload>
                  </Form.Item>
                </>
              )}

              <Alert
                message="证书要求"
                description="请确保证书格式正确，支持PEM格式的证书文件。证书链请包含完整的证书路径。"
                type="info"
                style={{ marginBottom: '16px' }}
              />

              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit">
                    上传证书
                  </Button>
                  <Button onClick={() => {
                    setSSLModalVisible(false);
                    sslForm.resetFields();
                    setSelectedDomain(null);
                    setSSLUploadMethod('text');
                  }}>
                    取消
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>

      {/* 基础防护购买/续费弹窗 */}
      <Modal
        title={isRenewal ? '续费基础防护服务' : '购买基础防护服务'}
        open={basicProtectionModalVisible}
        onCancel={() => {
          setBasicProtectionModalVisible(false);
          basicProtectionForm.resetFields();
          setIsRenewal(false);
        }}
        footer={null}
        width={500}
      >
        <Form
          form={basicProtectionForm}
          layout="vertical"
          onFinish={handleBasicProtectionPurchase}
        >
          {!selectedIP && (
            <Form.Item
              label="选择IP地址"
              name="ip"
              rules={[{ required: true, message: '请选择IP地址' }]}
            >
              <Select
                placeholder="请选择要购买服务的IP地址"
                onChange={(value) => {
                  const selectedIPRecord = availableIPs.find(ip => ip.ip === value);
                  if (selectedIPRecord?.hasWafService) {
                    setIsRenewal(true);
                  } else {
                    setIsRenewal(false);
                  }
                }}
              >
                {availableIPs.map(ipRecord => (
                  <Option key={ipRecord.ip} value={ipRecord.ip}>
                    <div>
                      <Text code>{ipRecord.ip}</Text>
                      {ipRecord.hasWafService && (
                        <Tag size="small" color="green" style={{ marginLeft: '8px' }}>
                          已购买防护
                        </Tag>
                      )}
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            label="有效期"
            name="duration"
            initialValue={1}
            rules={[
              { required: true, message: '请选择有效期' },
              { type: 'number', min: 1, message: '有效期必须大于0' }
            ]}
          >
            <Select>
              <Option value={1}>1个月</Option>
              <Option value={3}>3个月</Option>
              <Option value={6}>6个月</Option>
              <Option value={12}>12个月</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="支付方式"
            name="paymentMethod"
            initialValue="alipay"
            rules={[{ required: true, message: '请选择支付方式' }]}
          >
            <Select>
              <Option value="alipay">支付宝</Option>
            </Select>
          </Form.Item>

          <Alert
            message="重要提醒"
            description="开启WAF防护后，该IP地址的所有网站流量都将经过WAF系统。为确保网站正常访问，请务必将该IP下的所有站点域名添加到WAF系统中，未添加的域名将无法正常访问。"
            type="warning"
            showIcon
            style={{ marginBottom: '16px' }}
          />

          <Alert
            message="价格说明"
            description={
              isRenewal ?
                '续费基础防护服务：100元/月，延长现有服务时间，所有通过额度添加的域名防护时间将同步延长。' :
                '基础防护服务：100元/月，提供基本的WAF防护功能，赠送5个站点额度。'
            }
            type="info"
            style={{ marginBottom: '16px' }}
          />

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                提交订单
              </Button>
              <Button onClick={() => {
                setBasicProtectionModalVisible(false);
                basicProtectionForm.resetFields();
                setIsRenewal(false);
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 额外额度购买弹窗 */}
      <Modal
        title="购买额外站点额度"
        open={extraQuotaModalVisible}
        onCancel={() => {
          setExtraQuotaModalVisible(false);
          extraQuotaForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={extraQuotaForm}
          layout="vertical"
          onFinish={handleExtraQuotaPurchase}
        >
          {!selectedIP && (
            <Form.Item
              label="选择IP地址"
              name="ip"
              rules={[{ required: true, message: '请选择IP地址' }]}
            >
              <Select placeholder="请选择已购买基础防护的IP地址">
                {availableIPs.filter(ip => ip.hasWafService).map(ipRecord => (
                  <Option key={ipRecord.ip} value={ipRecord.ip}>
                    <div>
                      <Text code>{ipRecord.ip}</Text>
                      <Tag size="small" color="green" style={{ marginLeft: '8px' }}>
                        已购买防护
                      </Tag>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            label="购买数量"
            name="quotaCount"
            rules={[
              { required: true, message: '请输入购买数量' },
              { type: 'number', min: 1, message: '购买数量必须大于0' }
            ]}
          >
            <InputNumber
              min={1}
              placeholder="请输入购买域名额度数量"
              style={{ width: '100%' }}
              addonAfter="个域名"
            />
          </Form.Item>

          <Form.Item
            label="支付方式"
            name="paymentMethod"
            initialValue="alipay"
            rules={[{ required: true, message: '请选择支付方式' }]}
          >
            <Select>
              <Option value="alipay">支付宝</Option>
            </Select>
          </Form.Item>

          <Alert
            message="价格说明"
            description="额外站点额度：10元/站点/月，默认购买1个月，允许您添加更多域名到WAF防护中。注意：即使额外购买的额度未到期，一旦基础防护服务到期，所有额度都将无法使用。"
            type="info"
            style={{ marginBottom: '16px' }}
          />

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                提交订单
              </Button>
              <Button onClick={() => {
                setExtraQuotaModalVisible(false);
                extraQuotaForm.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 域名续费弹窗 */}
      <Modal
        title="域名续费"
        open={domainRenewModalVisible}
        onCancel={() => {
          setDomainRenewModalVisible(false);
          domainRenewForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={domainRenewForm}
          layout="vertical"
          onFinish={handleDomainRenew}
        >
          {!selectedIP && (
            <Form.Item
              label="选择IP地址"
              name="ip"
              rules={[{ required: true, message: '请选择IP地址' }]}
            >
              <Select placeholder="请选择已购买基础防护的IP地址">
                {availableIPs.filter(ip => ip.hasWafService).map(ipRecord => (
                  <Option key={ipRecord.ip} value={ipRecord.ip}>
                    <div>
                      <Text code>{ipRecord.ip}</Text>
                      <Tag size="small" color="green" style={{ marginLeft: '8px' }}>
                        已购买防护
                      </Tag>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            label="选择域名"
            name="domainId"
            rules={[{ required: true, message: '请选择要续费的域名' }]}
          >
            <Select
              placeholder="请选择要续费的域名"
              onChange={(domainId) => {
                const selectedDomainInfo = domains.find(d => d._id === domainId);
                if (selectedDomainInfo) {
                  const status = getDomainStatus(selectedDomainInfo);
                  const renewMessage = getRenewMessage(selectedDomainInfo);

                  if (selectedDomainInfo.quotaType === 'gift') {
                    message.warning(`【${status.label}】${renewMessage}`);
                  } else {
                    message.info(`【${status.label}】${renewMessage}`);
                  }
                }
              }}
            >
              {domains.filter(domain => shouldShowRenewButton(domain)).length === 0 ? (
                <Option disabled value="">
                  <Text type="secondary">暂无可续费的域名</Text>
                </Option>
              ) : domains.filter(domain => shouldShowRenewButton(domain)).map(domain => (
                <Option key={domain._id} value={domain._id}>
                  <div>
                    <Text code>{domain.domain}</Text>
                    <Tag size="small" color={getDomainStatus(domain).color} style={{ marginLeft: '8px' }}>
                      {getDomainStatus(domain).label}
                    </Tag>
                    {domain.hasIndependentExpiry && (
                      <Tag size="small" color="green" style={{ marginLeft: '4px' }}>
                        独立到期
                      </Tag>
                    )}
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>

          {domainRenewForm.getFieldValue('domainId') && (
            <Alert
              message="域名续费信息"
              description={(() => {
                const selectedDomainInfo = domains.find(d => d._id === domainRenewForm.getFieldValue('domainId'));
                if (!selectedDomainInfo) return '请选择要续费的域名';

                const expiredAt = getDisplayExpiredAt(selectedDomainInfo);
                const status = getDomainStatus(selectedDomainInfo);
                return (
                  <div>
                    <div>当前域名：{selectedDomainInfo.domain}</div>
                    <div>额度来源：{status.label}</div>
                    <div>当前到期时间：{expiredAt ? dayjs(expiredAt).format('YYYY-MM-DD HH:mm') : '未设置'}</div>
                    <div>续费说明：{getRenewMessage(selectedDomainInfo)}</div>
                    {selectedDomainInfo.quotaType === 'extra' && (
                      <div style={{ color: '#52c41a' }}>✓ 续费后该域名将拥有独立的到期时间</div>
                    )}
                  </div>
                );
              })()}
              type="info"
              style={{ marginBottom: '16px' }}
            />
          )}

          <Form.Item
            label="有效期"
            name="duration"
            initialValue={1}
            rules={[
              { required: true, message: '请选择有效期' },
              { type: 'number', min: 1, message: '有效期必须大于0' }
            ]}
          >
            <Select>
              <Option value={1}>1个月</Option>
              <Option value={3}>3个月</Option>
              <Option value={6}>6个月</Option>
              <Option value={12}>12个月</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="支付方式"
            name="paymentMethod"
            initialValue="alipay"
            rules={[{ required: true, message: '请选择支付方式' }]}
          >
            <Select>
              <Option value="alipay">支付宝</Option>
            </Select>
          </Form.Item>

          <Alert
            message="价格说明"
            description="域名续费：10元/站点/月，仅限额外购买额度的域名（第6个及以后）。续费后该域名将拥有独立的到期时间。重要提醒：即使域名单独续费未到期，一旦基础防护服务到期，该域名也将无法使用。注意：前5个赠送额度的域名无需单独续费，跟随基础防护服务自动延期。"
            type="info"
            style={{ marginBottom: '16px' }}
          />

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                提交订单
              </Button>
              <Button onClick={() => {
                setDomainRenewModalVisible(false);
                domainRenewForm.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 使用帮助弹窗 */}
      <Modal
        title="WAF防护管理系统使用帮助"
        open={helpModalVisible}
        onCancel={() => setHelpModalVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setHelpModalVisible(false)}>
            我知道了
          </Button>
        ]}
        width={800}
      >
        <div style={{ lineHeight: '1.8', fontSize: '14px' }}>
          <div style={{ marginBottom: '24px' }}>
            <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>📋 系统功能概述</Text>
            <div style={{ marginTop: '8px', paddingLeft: '16px' }}>
              <div>• <Text strong>防护概览</Text>：查看您的WAF防护服务整体状况和统计信息</div>
              <div>• <Text strong>IP服务管理</Text>：管理不同IP地址的WAF防护服务状态和配置</div>
              <div>• <Text strong>域名管理</Text>：管理受保护的域名，包括添加、编辑、SSL证书配置</div>
            </div>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>🛡️ 基础防护服务</Text>
            <div style={{ marginTop: '8px', paddingLeft: '16px' }}>
              <div>• <Text strong>价格</Text>：100元/月，为单个IP地址提供基础WAF防护</div>
              <div>• <Text strong>赠送额度</Text>：购买后自动获得5个域名额度（前5个域名免费）</div>
              <div>• <Text strong>重要提醒</Text>：开启WAF防护后，该IP下的所有站点域名都需要添加到系统中，否则将无法正常访问</div>
              <div>• <Text strong>到期影响</Text>：基础防护到期后，所有通过额度添加的域名将同时停止防护</div>
              <div>• <Text strong>续费说明</Text>：续费基础防护会同时延长所有额度域名的防护时间</div>
            </div>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <Text strong style={{ fontSize: '16px', color: '#722ed1' }}>📈 额外额度购买</Text>
            <div style={{ marginTop: '8px', paddingLeft: '16px' }}>
              <div>• <Text strong>适用场景</Text>：当需要保护超过5个域名时，可购买额外额度</div>
              <div>• <Text strong>价格</Text>：10元/站点/月，第6个及以后的每个域名，默认购买1个月</div>
              <div>• <Text strong>续费选择</Text>：额外购买的域名可以单独续费，获得独立的到期时间</div>
              <div>• <Text strong>重要限制</Text>：即使额外购买的额度未到期，一旦基础防护服务到期，所有额度都将无法使用</div>
            </div>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <Text strong style={{ fontSize: '16px', color: '#fa8c16' }}>🌐 域名管理</Text>
            <div style={{ marginTop: '8px', paddingLeft: '16px' }}>
              <div>• <Text strong>添加域名</Text>：配置域名、协议（HTTP/HTTPS）和端口，源服务器地址固定为当前选择的IP</div>
              <div>• <Text strong>DNS配置</Text>：添加域名后，通过工单联系我们进行策略部署</div>
              <div>• <Text strong>SSL证书</Text>：HTTPS域名需要上传SSL证书，支持文本输入或文件上传</div>
              <div>• <Text strong>域名状态</Text>：可以启用/禁用域名防护，删除不需要的域名释放额度</div>
            </div>
          </div>



          <div style={{ marginBottom: '16px' }}>
            <Text strong style={{ fontSize: '16px', color: '#f5222d' }}>❗ 重要提醒</Text>
            <div style={{ marginTop: '8px', paddingLeft: '16px' }}>
              <div>• <Text strong style={{ color: '#f5222d' }}>开启WAF防护后，必须将该IP下的所有站点域名添加到系统中，否则将无法正常访问</Text></div>
              <div>• 基础防护服务到期后，该IP下所有域名防护将停止</div>
              <div>• 删除域名会立即释放对应的域名额度，可重新使用</div>
              <div>• SSL证书到期前请及时更新，避免HTTPS访问异常</div>
              <div>• 如遇问题，请及时通过工单联系技术支持</div>
            </div>
          </div>

          <Alert
            message="技术支持"
            description="如有任何问题或需要技术支持，请通过工单系统联系我们。我们会及时为您提供专业的技术支持和解决方案。"
            type="info"
            showIcon
          />
        </div>
      </Modal>
      </div>
    </div>
  );
};

export default WafManagement; 